/**
 * Limited-time events — biggest mobile-RPG retention driver. Each event
 * has a fixed start + end window and applies bonus multipliers to the
 * game's existing reward streams (gold, XP, drop rarity, egg chance, etc).
 *
 * Adding a new event = one entry below. No migration, no rebuild — just
 * give it a start/end ISO timestamp and the multipliers it applies, and
 * the engine handles surfacing it in the UI + multiplying rewards.
 *
 * Active-event detection runs on every load + every 5 min thereafter
 * (in App.tsx). The active event surfaces as a banner on the Town screen
 * with the time remaining, and its multipliers compose with all other
 * sources (premium, pets, house, guild perks, weather) via bonuses.ts.
 */

/**
 * ── THE EVENT FRAMEWORK (2026-10-12) ─────────────────────────────────────
 *
 * Events were multiplier-only: a window, a banner, and a number applied to
 * the reward streams. That is a good weekend event and nothing more — you
 * cannot build Festival of Hearts out of "+50% gold".
 *
 * Three optional parts turn a multiplier window into an event:
 *
 *   currency  — a soft currency earned from the activities the event is
 *               about, which EXPIRES with it
 *   track     — milestones against that currency, every reward claimable
 *   shop      — the Shop's Event tab, spending the currency
 *
 * All three are optional and independent. A Double XP Weekend declares none
 * of them and behaves exactly as it does today; Winter Festival declares all
 * three. See EVENTS_PLAN.md for the thirteen planned events.
 */

/** A soft currency that lives and dies with one event. */
export interface EventCurrency {
  /** Stable id, also the save key. */
  id: string
  /** "Snowflakes" — plural, it is always shown as a count. */
  name: string
  /** Emoji fallback until the sprite lands. */
  emoji: string
  /** UISprite under `ui/events/`. */
  sprite: string
  /**
   * How much one of each action is worth. Anything omitted earns nothing,
   * which is what makes an event ABOUT something: a fishing event pays for
   * fish and not for kills.
   */
  earn: {
    kill?: number
    bossKill?: number
    /**
     * An enemy wearing a live event's VARIANT, paid instead of `kill`.
     * Same reasoning as `eventFish`: without it, killing the thing the
     * event put in the world is worth what killing anything is worth.
     */
    eventKill?: number
    gather?: number
    craft?: number
    /**
     * A craft of one of the EVENT's own recipes, paid instead of `craft`.
     * Fifth instance of the split (eventFish / eventKill / eventDig /
     * eventDungeon) and the reasoning has not moved: without it, Forgeworks
     * would reward crafting rather than rewarding Forgeworks, and its
     * commission bench would be the slowest way to play its own event.
     */
    eventCraft?: number
    /**
     * A tower floor cleared while the Twisted Tower event runs. There is
     * no ordinary-activity twin — the tower paid an event nothing before
     * this — so like `eventFind` it is a declaration rather than a split,
     * and declaring it is what switches the daily twist on.
     *
     * The DAY'S TWIST scales it (`rewardMult`), which is where the trade
     * lives: Ponderous doubles the pay for a harder climb, Hollow pays 40%
     * less for an easier one.
     */
    eventTowerFloor?: number
    /** Any fish landed. */
    fish?: number
    /**
     * A fish belonging to a LIVE event, paid INSTEAD of `fish`.
     *
     * Without this an event fish was worth exactly what a cod was, so a
     * fishing event rewarded fishing rather than rewarding the event.
     * Reported as "i want the event worth it not fishing for something
     * thats already there".
     */
    eventFish?: number
    dig?: number
    /**
     * A tile dug at a live event's own site, paid instead of `dig`.
     * Third instance of the same split (eventFish, eventKill): without it
     * the event's temporary mine is worth no more than the permanent one
     * next to it, and the event rewards the ACTIVITY rather than itself.
     */
    eventDig?: number
    dungeonClear?: number
    /**
     * A clear of a dungeon that belongs to a live event, paid instead of
     * `dungeonClear`. Same reasoning as `eventFish` and `eventKill`: an
     * event whose content IS a temporary dungeon must not pay the same for
     * the eighteen permanent ones, or the event is optional to itself.
     */
    eventDungeon?: number
    /**
     * A SPRING HUNT cache opened. There is no ordinary-activity twin for
     * this one — unlike eventFish/eventKill/eventDig/eventDungeon, nothing
     * outside the event hides caches, so there is nothing to split from.
     * Declaring a rate is also what TURNS THE HUNT ON: `huntTick` skips any
     * event without one, so no event accidentally starts hiding eggs.
     */
    eventFind?: number
    /**
     * The pet-care loop — things a player CHOOSES to do to a pet.
     * `gainPetExp` is deliberately not among them: it fires on every fight,
     * so earning from it would be earning from combat in a pet costume.
     */
    feed?: number
    hatch?: number
    petStar?: number
    petMerge?: number
  }
}

/** One rung of an event track. */
export interface EventMilestone {
  /** Total currency earned (cumulative) that unlocks this rung. */
  at: number
  /** What it pays. Mirrors QuestReward so the existing payer can be reused. */
  reward: {
    gold?: number
    diamonds?: number
    items?: { itemId: number; quantity: number }[]
    /** Granted whole, like a mailed pet. */
    pets?: { petId: number }[]
    /** Cosmetic skin id from data/cosmetics.ts. */
    cosmeticId?: number
    /**
     * A CLASS-LOCKED SET of skin ids — the player is granted the one that
     * matches their class, via `cosmeticSetFor`.
     *
     * Why this exists instead of just using `cosmeticId`: skins are
     * class-locked, so a single id is a reward that five of the seven
     * classes grind a whole track for and then cannot equip. Haunted
     * Realms is the first event to hand one over and it ships one skin per
     * class rather than the plan's five for that reason.
     */
    cosmeticSet?: number[]
    /**
     * A PORTRAIT FRAME id (data/portraitFrames.ts) — the first
     * class-agnostic cosmetic reward, so unlike `cosmeticId` and
     * `cosmeticSet` it needs no per-class resolution at all. One id, every
     * class, and it survives a class change.
     */
    frameId?: string
    /** Title id from data/titles.ts. */
    titleId?: string
  }
  /**
   * This rung also needs the event's SERVER GOAL reached.
   *
   * The whole point of a shared goal is that it changes what you can
   * have, so it gates the three rungs the dragon itself pays. Earning
   * the currency is still on you; hatching the egg is on everybody.
   *
   * Read in exactly ONE place (the track renderer in EventScreen), and
   * `.verify/check_dragon_awakening.ts` asserts that \u2014 a gate
   * declared here and honoured nowhere is this session's most common
   * bug by a wide margin.
   */
  requiresGoal?: boolean
  /** Short label for the track row. */
  label: string
}

/**
 * One line of an event's shop.
 *
 * ── WHY CURATED AND CLIENT-SIDE, NOT THE 0125 ROLLER ─────────────────────
 * EVENTS_PLAN.md said `get_or_roll_shop_offers` would be repurposed to roll
 * event stock. Two things killed that on contact:
 *
 *   1. `purchase_shop_offer` does an atomic GOLD/DIAMOND deduct. Event
 *      currency lives in the save, so the RPC cannot take payment for it,
 *      and teaching it to would mean putting the currency on the server —
 *      a lot of machinery for a cosmetic event in a game whose gold and
 *      items are already client-authoritative.
 *   2. `shop_offers.item_id` is an integer. An event shop needs to sell
 *      PETS and COSMETICS too, which are separate id spaces.
 *
 * And rolling was the wrong instinct anyway: a daily vendor is random
 * because its job is to smooth a grind, but an event shop's stock IS part
 * of the event's design. Curated, authored next to the track it complements.
 * Everyone sees the same shop because everyone sees the same event.
 *
 * The 0125 roller stays in the database, unused, if server-authoritative
 * stock is ever wanted.
 */
export interface EventShopOffer {
  /** Stable key — the per-player purchase count hangs off it. */
  id: string
  /** Exactly one of these four. */
  itemId?: number
  petId?: number
  cosmeticId?: number
  /** A class-locked set — the buyer gets the one for their class. See
   *  `EventMilestone.reward.cosmeticSet`. */
  cosmeticSet?: number[]
  /** A portrait frame id. Class-agnostic — see the reward field. */
  frameId?: string
  /** Bundle size, for items. Defaults to 1. */
  quantity?: number
  /** Price in the event's currency. */
  cost: number
  /** How many a player may buy for the whole event. 0 = unlimited. */
  maxPerPlayer: number
}

/**
 * A SERVER-WIDE SHARED GOAL. One event has one; twelve do not.
 *
 * The thirteenth event's brief is "Server hatches one egg -> world boss",
 * which is the only thing in the calendar that cannot live in the save: what
 * YOU do has to change what EVERYONE sees. Backed by migration 0128 and
 * services/eventGoal.ts.
 *
 * ── WHY THERE ARE TWO THRESHOLDS ─────────────────────────────────────────
 * Local-only mode is a supported way to play this game and a guest has no
 * account, so for some players there is no server to count toward. A single
 * server-scale threshold would mean those players watch a bar that can never
 * fill, which is worse than not having the feature.
 *
 * So `solo` is the same goal sized for one person. The event is then a
 * personal hatch rather than a communal one, the panel says which it is
 * drawing, and nobody is shown a locked reward they had no route to.
 */
export interface EventServerGoal {
  /** Shared total needed. THE population-dependent number \u2014 see events.ts. */
  threshold: number
  /** The same goal for a player with no server. Reachable by one person. */
  solo: number
  /** "The egg" \u2014 what the bar is filling, for the panel's copy. */
  noun: string
  /** One line on what reaching it does. */
  effect: string
}

export interface GameEvent {
  /** Stable id so the badge / "have you participated?" check is reliable. */
  id: string
  /** Display name for the banner. */
  name: string
  /** One-line vibe text. */
  description: string
  /** Emoji icon shown in the banner. */
  emoji: string
  /** Accent color for the banner border / glow. */
  color: string
  /** ISO timestamp the event becomes active (inclusive). */
  startAt: string
  /** ISO timestamp the event ends (exclusive). */
  endAt: string
  /**
   * Multipliers applied to specific reward streams while the event is
   * active. All default to 1 (no effect). Mixed in with the rest of
   * the bonuses pipeline in src/services/game/bonuses.ts via the
   * active-event lookup helper below.
   */
  multipliers: {
    gold?: number
    xp?: number
    petXp?: number
    skillXp?: number
    eggDrop?: number       // multiplies egg drop chance from kills
    rareDrop?: number      // multiplies luckMultiplier
  }
  /** Present on content events. Omitted = a multiplier window, as before. */
  currency?: EventCurrency
  /**
   * Hidden-cache tuning, for an event that declares `earn.eventFind`.
   *
   * Omitted = the Spring Hunt defaults (2–4 caches per zone, 4–9 actions
   * apart). The seed namespace is NEVER set here — it is always the event
   * id, so two hunts cannot hide in the same places. See springHunt.ts.
   */
  hunt?: { cacheMin?: number; cacheMax?: number; windowMin?: number; windowMax?: number }
  /** A server-wide shared goal. Only Dragon Awakening has one. */
  serverGoal?: EventServerGoal
  /** Milestones, ascending by `at`. Requires `currency`. */
  track?: EventMilestone[]
  /** The Shop's Event tab. Requires `currency`. */
  shop?: EventShopOffer[]
}

/**
 * The roster. Add new entries any time — they activate automatically on
 * their startAt timestamp. To run an event NOW you can edit startAt to
 * a past time + endAt to a future time and reload the page.
 *
 * Recurring events (e.g. weekly double-XP) can be added as repeated
 * entries with different dates; eventually we'll move this to a JSONB
 * table the admin edits without a code push.
 */
export const EVENTS: GameEvent[] = [
  // Sample currently-active event so the system has something to surface
  // while the live admin tool is being built. Lasts ~1 week from a fixed
  // anchor; expire it by setting endAt to a past timestamp.
  {
    id: 'launch-hatchfest-2026-06',
    name: 'Hatchfest',
    description: '+50% egg drop chance from every kill — for one week only.',
    emoji: '🐣',
    color: '#a855f7',
    startAt: '2026-06-09T00:00:00Z',
    endAt:   '2026-06-16T00:00:00Z',
    multipliers: { eggDrop: 1.5 },
  },

  {
    id: 'goldrush-2026-07',
    name: 'Goldrush',
    description: '+50% gold from every source — kills, gathering, quests, marketplace, the works.',
    emoji: '🪙',
    color: '#fbbf24',
    startAt: '2026-07-01T00:00:00Z',
    endAt:   '2026-07-04T00:00:00Z',
    multipliers: { gold: 1.5 },
  },

  {
    // Fills the dead window between Goldrush (ends 07-04) and Double XP
    // Weekend (starts 07-12) so there's never a "no event" lull in July.
    id: 'artisans-week-2026-07',
    name: "Artisan's Week",
    description: '+50% skill XP from every gather, craft, and refine. Level those professions!',
    emoji: '🛠️',
    color: '#fb923c',
    startAt: '2026-07-04T00:00:00Z',
    endAt:   '2026-07-12T00:00:00Z',
    multipliers: { skillXp: 1.5 },
  },

  {
    id: 'double-xp-weekend-2026-07',
    name: 'Double XP Weekend',
    description: '+100% combat + skill XP. Pets level twice as fast too.',
    emoji: '⭐',
    color: '#06b6d4',
    startAt: '2026-07-12T00:00:00Z',
    endAt:   '2026-07-15T00:00:00Z',
    multipliers: { xp: 2.0, skillXp: 2.0, petXp: 2.0 },
  },

  {
    id: 'rare-rush-2026-08',
    name: 'Rare Rush',
    description: '+75% chance of rare+ rolls on loot and gathering drops.',
    emoji: '✨',
    color: '#f59e0b',
    startAt: '2026-08-01T00:00:00Z',
    endAt:   '2026-08-04T00:00:00Z',
    multipliers: { rareDrop: 1.75 },
  },

  // ══════════════════════════════════════════════════════════════════════
  // WINTER FESTIVAL — event 1 of 13 (see EVENTS_PLAN.md)
  //
  // The first CONTENT event: currency, track and shop, not just a
  // multiplier. Dates are a placeholder — it runs for three weeks over the
  // turn of the year and will be set properly once the release date is.
  //
  // ── WHY SNOWFLAKES COME FROM GATHERING AND KILLS, NOT EVERYTHING ──────
  // An event has to be ABOUT something. Snowflakes fall to anyone out in
  // the world — fighting or gathering — because that is what "winter has
  // come to the realms" means, and it keeps the event open to a player who
  // does not fight. Crafting and fishing pay nothing here; those are
  // Forgeworks' and Fishing Week's identities and handing them out
  // everywhere would make all thirteen events feel the same.
  //
  // ── THE TRACK ─────────────────────────────────────────────────────────
  // 12 rungs to 6,000. Tuned so a moderate player clears it across the full
  // three weeks rather than an optimal one clearing it in three days — the
  // catch-up curve (eventProgress.ts) carries anyone who starts late.
  // Pets land at 5 rungs spread through it so there is always a next one in
  // sight; the frost phoenix sits at the top as the only legendary.
  {
    id: 'winter-festival-2026',
    name: 'Frostpine Winter Festival',
    description: 'Snow has reached every realm. Gather snowflakes, and something old stirs under the ice.',
    emoji: '❄️',
    color: '#7dd3fc',
    startAt: '2026-12-15T00:00:00Z',
    endAt:   '2027-01-05T00:00:00Z',
    multipliers: { gold: 1.25 },
    currency: {
      id: 'snowflake',
      name: 'Snowflakes',
      emoji: '❄️',
      sprite: 'snowflake',
      // eventDungeon: the Frostpine Hollow, one attempt a day. 250 x 21
      // days = 5,250 against the 6,000 track below, so a player who shows up
      // for the boss daily all but finishes the track - and still needs
      // ordinary play for the 13,400 shop. An ORDINARY dungeon keeps paying
      // 60, a quarter of it, so the event dungeon is the one worth the run.
      earn: { kill: 3, bossKill: 40, gather: 5, dungeonClear: 60, eventDungeon: 250 },
    },
    // The shop sells what the track does NOT, so the two are reasons to
    // keep playing rather than two routes to the same pets. Everything here
    // is finite (maxPerPlayer) so a player cannot convert a long grind into
    // an unbounded pile, and the two shop-only pets are the reason to spend
    // at all rather than hoard.
    shop: [
      { id: 'wf-pet-robin',   petId: 704, cost: 1_800, maxPerPlayer: 1 },
      { id: 'wf-pet-drake',   petId: 707, cost: 2_600, maxPerPlayer: 1 },
      { id: 'wf-pet-wolf',    petId: 709, cost: 3_400, maxPerPlayer: 1 },
      { id: 'wf-pet-fox',     petId: 705, cost: 2_200, maxPerPlayer: 1 },
      { id: 'wf-pet-fawn',    petId: 703, cost: 1_200, maxPerPlayer: 1 },
      { id: 'wf-pet-goat',    petId: 698, cost:   400, maxPerPlayer: 1 },
      { id: 'wf-pet-bunting', petId: 697, cost:   400, maxPerPlayer: 1 },
      { id: 'wf-pet-penguin', petId: 699, cost:   700, maxPerPlayer: 1 },
      { id: 'wf-pet-snowman', petId: 700, cost:   700, maxPerPlayer: 1 },
    ],
    track: [
      { at:   100, label: 'A first flurry',      reward: { gold: 25_000 } },
      { at:   300, label: 'Snow-hare',           reward: { pets: [{ petId: 696 }] } },
      { at:   600, label: 'Warm hands',          reward: { gold: 60_000, diamonds: 25 } },
      { at: 1_000, label: 'Frost ermine',        reward: { pets: [{ petId: 701 }] } },
      { at: 1_500, label: 'Deep winter',         reward: { gold: 120_000 } },
      { at: 2_100, label: 'Ice owl',             reward: { pets: [{ petId: 702 }] } },
      { at: 2_800, label: 'The long night',      reward: { diamonds: 75 } },
      { at: 3_500, label: 'Yeti cub',            reward: { pets: [{ petId: 706 }] } },
      { at: 4_200, label: 'Hoarfrost',           reward: { gold: 250_000 } },
      { at: 5_000, label: 'Crystal stag',        reward: { pets: [{ petId: 708 }] } },
      { at: 5_500, label: 'Winter’s Own',       reward: { titleId: 'winters-own' } },
      { at: 6_000, label: 'Frost phoenix',       reward: { pets: [{ petId: 710 }], diamonds: 150 } },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // FESTIVAL OF HEARTS — event 2 of 13
  //
  // THE TEST OF WHETHER THE FRAMEWORK IS A FRAMEWORK. Winter Festival was
  // built alongside the engine, so of course it fitted. Hearts was specified
  // independently ("Collect petals through combat, fishing and mining; fight
  // a Heartbroken Guardian") and the only engine work it needed was two more
  // listeners in eventCurrencyMiddleware — which are now all five, wired at
  // once rather than dripped in per event.
  //
  // ── WHY ITS EARN TABLE IS DELIBERATELY WIDE ─────────────────────
  // Winter pays for kills and gathering; Hearts pays for combat, fishing AND
  // digging. That is the brief, and it is also what stops the thirteen events
  // feeling identical: each one points at a different corner of the game, so
  // a player who lives in the fishing minigame has an event that is FOR them
  // and several that are not.
  //
  // Rates are per-action and tuned so each route reaches the track at about
  // the same pace — a fish takes far longer than a kill, so it pays far more.
  {
    id: 'festival-of-hearts-2027',
    name: 'Festival of Hearts',
    description: 'Petals are falling everywhere, and something under the old rose-garden is awake and very unhappy about it.',
    emoji: '💕',
    color: '#f472b6',
    startAt: '2027-02-07T00:00:00Z',
    endAt:   '2027-02-21T00:00:00Z',
    multipliers: { rareDrop: 1.25 },
    currency: {
      id: 'petal',
      name: 'Petals',
      emoji: '🌸',
      sprite: 'petal',
      // eventDungeon: the Briar Garden, one attempt a day. A fortnight
      // rather than three weeks, so slightly more per clear: 280 x 14 =
      // 3,920 against the 5,000 track. No `dungeonClear` here deliberately -
      // this event is about fishing and digging, so an ordinary dungeon
      // should pay nothing and the Garden should be the only delve that does.
      earn: { kill: 3, bossKill: 40, fish: 12, dig: 10, eventDungeon: 280 },
    },
    // 14 days, not 21 — a monthly event, shorter than a seasonal one, so the
    // track is proportionally smaller. Same 12 rungs.
    track: [
      { at:   100, label: 'First bloom',       reward: { gold: 25_000 } },
      { at:   250, label: 'Posy',              reward: { pets: [{ petId: 711 }] } },
      { at:   500, label: 'A quiet word',      reward: { gold: 60_000, diamonds: 25 } },
      { at:   850, label: 'Dovetail',          reward: { pets: [{ petId: 714 }] } },
      { at: 1_250, label: 'Rose-light',        reward: { gold: 120_000 } },
      { at: 1_700, label: 'Lovelorn',          reward: { pets: [{ petId: 717 }] } },
      { at: 2_200, label: 'The garden stirs',  reward: { diamonds: 75 } },
      { at: 2_750, label: 'Thornheart',        reward: { pets: [{ petId: 720 }] } },
      { at: 3_300, label: 'Briar and bloom',   reward: { gold: 250_000 } },
      { at: 3_900, label: 'Valentine',         reward: { pets: [{ petId: 724 }] } },
      { at: 4_400, label: 'Heart Unbroken',    reward: { titleId: 'heart-unbroken' } },
      { at: 5_000, label: 'Amoret',            reward: { pets: [{ petId: 725 }], diamonds: 150 } },
    ],
    // Same split as Winter: the track gives six, the shop sells the other
    // nine, so the two never compete to hand over the same pet.
    shop: [
      { id: 'foh-pet-sugarbeak',  petId: 712, cost:   350, maxPerPlayer: 1 },
      { id: 'foh-pet-thistle',    petId: 713, cost:   350, maxPerPlayer: 1 },
      { id: 'foh-pet-marzipan',   petId: 715, cost:   600, maxPerPlayer: 1 },
      { id: 'foh-pet-quiverfoot', petId: 716, cost:   600, maxPerPlayer: 1 },
      { id: 'foh-pet-garland',    petId: 718, cost: 1_000, maxPerPlayer: 1 },
      { id: 'foh-pet-ribbonkite', petId: 719, cost: 1_000, maxPerPlayer: 1 },
      { id: 'foh-pet-sweetheart', petId: 721, cost: 1_800, maxPerPlayer: 1 },
      { id: 'foh-pet-cupidon',    petId: 722, cost: 2_200, maxPerPlayer: 1 },
      { id: 'foh-pet-heartstring',petId: 723, cost: 2_800, maxPerPlayer: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // LEGENDARY FISHING WEEK — event 3 of 13
  //
  // The least combat-shaped event on the list, chosen third on purpose: if
  // the framework survives this it will survive the rest.
  //
  // ── WHAT IT PROVES ──────────────────────────────────────────
  // • A WEEK, not a fortnight or three. The track scales to the window; the
  //   framework has no opinion about length.
  // • NO SHOP, deliberately. The framework's three parts are meant to be
  //   optional and this is the first event to test that — the currency here
  //   buys nothing because the COLLECTION is the reward, and a shop would be
  //   a second thing to spend a short week chasing.
  // • NO BOSS. Nothing in the engine assumed one.
  // • Its collection lives in a system that already exists: the nine event
  //   fish drop into the normal fishing codex (`fishing.caught`), so the
  //   journal is a FILTERED VIEW of state the game already keeps, not new
  //   state. That is what "a collection journal" costs when the catalogue
  //   it belongs to is already built.
  //
  // ── EARNS FROM FISHING ALONE ─────────────────────────────────
  // No kills, no gathering. A player who never casts earns nothing, and that
  // is the point: thirteen events that all pay for killing things are one
  // event thirteen times. The cast is open to anyone from the first hour, so
  // nobody is locked out — only pointed somewhere.
  {
    id: 'legendary-fishing-week-2027',
    name: 'Legendary Fishing Week',
    description: 'Strange fish have come inshore. Nine of them, and one that should not exist.',
    emoji: '🎣',
    color: '#38bdf8',
    startAt: '2027-03-15T00:00:00Z',
    endAt:   '2027-03-22T00:00:00Z',
    multipliers: {},
    currency: {
      id: 'pearl',
      name: 'Pearls',
      emoji: '🫧',
      sprite: 'pearl',
      // Ordinary fish still trickle, so a bad cast is not a dead cast — but
      // an event fish is worth twelve of one. The week is ABOUT the nine.
      earn: { fish: 5, eventFish: 60 },
    },
    // Seven days and one activity, so the numbers are far smaller than a
    // three-week all-comers event. 8 rungs, not 12.
    track: [
      { at:   100, label: 'Lines out',        reward: { gold: 20_000 } },
      { at:   260, label: 'A good morning',   reward: { gold: 50_000 } },
      { at:   480, label: 'Deep water',       reward: { diamonds: 40 } },
      { at:   760, label: 'Something big',    reward: { gold: 120_000 } },
      { at: 1_080, label: 'The regulars',     reward: { diamonds: 60 } },
      { at: 1_440, label: 'Record board',     reward: { gold: 250_000 } },
      { at: 1_760, label: 'Master Angler',    reward: { titleId: 'master-angler' } },
      { at: 2_100, label: 'Gaffer',           reward: { pets: [{ petId: 726 }], diamonds: 120 } },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // COMPANION FESTIVAL — event 4 of 13
  //
  // The last major system the framework had not touched: PET CARE. It needed
  // four new earn signals (feed / hatch / petStar / petMerge) and nothing
  // else — no new state, no new screen, no special-casing.
  //
  // ── WHY NOT gainPetExp ─────────────────────────────────────
  // It is by far the busiest pet action, and it fires on every fight. An
  // event earning from it would be earning from COMBAT in a pet costume,
  // which is the one thing the thirteen-event plan is trying to avoid. The
  // four signals here are things a player chooses to do TO a pet.
  //
  // ── THE RATES ARE INVERTED FROM WHAT YOU WOULD GUESS ──────────────
  // Feeding is frequent and cheap, so it pays least. Merging destroys a pet
  // and a star-up costs real gold, so those pay most. The event should
  // reward the care a player would NOT otherwise bother with, not the one
  // they already do on a timer.
  {
    id: 'companion-festival-2027',
    name: 'Companion Festival',
    description: 'A week for the animals. Feed them, raise them, and see who turns up at the edge of camp.',
    emoji: '🐾',
    color: '#fbbf24',
    startAt: '2027-05-10T00:00:00Z',
    endAt:   '2027-05-24T00:00:00Z',
    multipliers: { petXp: 1.5 },
    currency: {
      id: 'ribbon',
      name: 'Ribbons',
      emoji: '🎀',
      sprite: 'ribbon',
      // Far higher per-action than a combat event's rates, because pet care
      // is LOW VOLUME: a player feeds a handful a day and stars one up far
      // less than daily. Rates tuned against that, not against a kill count.
      earn: { feed: 20, hatch: 120, petStar: 300, petMerge: 220 },
    },
    track: [
      { at:   100, label: 'Open house',        reward: { gold: 25_000 } },
      { at:   250, label: 'Tagalong',          reward: { pets: [{ petId: 727 }] } },
      { at:   500, label: 'Full bowls',        reward: { gold: 60_000, diamonds: 25 } },
      { at:   850, label: 'Whistlejack',       reward: { pets: [{ petId: 730 }] } },
      { at: 1_250, label: 'Good company',      reward: { gold: 120_000 } },
      { at: 1_700, label: 'Bellhop',           reward: { pets: [{ petId: 733 }] } },
      { at: 2_200, label: 'The long walk',     reward: { diamonds: 75 } },
      { at: 2_750, label: 'Understudy',        reward: { pets: [{ petId: 736 }] } },
      { at: 3_300, label: 'Hearthside',        reward: { gold: 250_000 } },
      { at: 3_900, label: 'Oldboy',            reward: { pets: [{ petId: 740 }] } },
      { at: 4_400, label: 'Best in Show',      reward: { titleId: 'best-in-show' } },
      { at: 5_000, label: 'Faithful',          reward: { pets: [{ petId: 741 }], diamonds: 150 } },
    ],
    shop: [
      { id: 'cf-pet-pocketmouse', petId: 728, cost:   350, maxPerPlayer: 1 },
      { id: 'cf-pet-lapwarmer',   petId: 729, cost:   350, maxPerPlayer: 1 },
      { id: 'cf-pet-breadcrumb',  petId: 731, cost:   600, maxPerPlayer: 1 },
      { id: 'cf-pet-scrapper',    petId: 732, cost:   600, maxPerPlayer: 1 },
      { id: 'cf-pet-nestle',      petId: 734, cost: 1_000, maxPerPlayer: 1 },
      { id: 'cf-pet-rolypoly',    petId: 735, cost: 1_000, maxPerPlayer: 1 },
      { id: 'cf-pet-hearthcat',   petId: 737, cost: 1_800, maxPerPlayer: 1 },
      { id: 'cf-pet-stray',       petId: 738, cost: 2_200, maxPerPlayer: 1 },
      { id: 'cf-pet-longpaw',     petId: 739, cost: 2_800, maxPerPlayer: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // BLOOD MOON — event 5 of 13
  //
  // The event that builds the ENEMY VARIANT LAYER, which Haunted Realms and
  // Echo Realm Rift both need. A variant is a pure transform over an
  // existing enemy — name, colour, stat multipliers — so the whole thing
  // costs no new enemy rows and no new enemy art, which is the only reason
  // three events can afford it. See services/game/enemyVariants.ts.
  //
  // ── SHORT, AND TWICE ───────────────────────────────────────
  // Four days, not two weeks. "Special enemy variants appear throughout the
  // realms for a few days" is a WEEKEND shape, and a world-altering event
  // that runs a fortnight stops being altered and just becomes the world.
  // The variant roster reseeds daily, so each of the four days has a
  // different third of the bestiary wearing red.
  //
  // ── EARNS FROM VARIANTS, NOT FROM KILLING ──────────────────────
  // Same lesson as the fish: an ordinary kill pays 2, a Blood-touched kill
  // pays 30. Without the split the event would reward combat rather than
  // rewarding the event, and a player could ignore the red enemies entirely
  // — which are the harder fight — and progress just as fast.
  {
    id: 'blood-moon-2027',
    name: 'Blood Moon',
    description: 'A third of everything out there has turned, and it is worse than it was. It passes in four days.',
    emoji: '🌑',
    color: '#dc2626',
    startAt: '2027-11-05T00:00:00Z',
    endAt:   '2027-11-09T00:00:00Z',
    multipliers: { rareDrop: 1.4 },
    currency: {
      id: 'bloodmark',
      name: 'Bloodmarks',
      emoji: '🩸',
      sprite: 'bloodmark',
      earn: { kill: 2, eventKill: 30, bossKill: 60 },
    },
    // Four days, so a SHORT track — 8 rungs, the Fishing Week shape rather
    // than the fortnight shape.
    track: [
      { at:   150, label: 'The moon rises',   reward: { gold: 30_000 } },
      { at:   400, label: 'Nipper',           reward: { pets: [{ petId: 742 }] } },
      { at:   750, label: 'Red night',        reward: { diamonds: 40 } },
      { at: 1_150, label: 'Lychwing',         reward: { pets: [{ petId: 746 }] } },
      { at: 1_600, label: 'Deep red',         reward: { gold: 150_000 } },
      { at: 2_100, label: 'Crimsonmaw',       reward: { pets: [{ petId: 749 }] } },
      { at: 2_600, label: 'Moonsworn',        reward: { titleId: 'moonsworn' } },
      { at: 3_100, label: 'Mourningstar',     reward: { pets: [{ petId: 756 }], diamonds: 150 } },
    ],
    // Priced for FOUR DAYS, not a fortnight. The first pass reused the
    // monthly-event price ladder and put the full set at 16,900 against the
    // ~16,680 even a dedicated player can earn in the window — so the last
    // pet was unreachable by anyone. The shop scales to the window, not to
    // a habit.
    shop: [
      { id: 'bm-pet-candlemoth',  petId: 743, cost:   200, maxPerPlayer: 1 },
      { id: 'bm-pet-grue',        petId: 744, cost:   200, maxPerPlayer: 1 },
      { id: 'bm-pet-hemlock',     petId: 745, cost:   400, maxPerPlayer: 1 },
      { id: 'bm-pet-sanguine',    petId: 747, cost:   400, maxPerPlayer: 1 },
      { id: 'bm-pet-nightjar',    petId: 748, cost:   650, maxPerPlayer: 1 },
      { id: 'bm-pet-pallor',      petId: 750, cost:   650, maxPerPlayer: 1 },
      { id: 'bm-pet-vespers',     petId: 751, cost: 1_050, maxPerPlayer: 1 },
      { id: 'bm-pet-thrall',      petId: 752, cost: 1_250, maxPerPlayer: 1 },
      { id: 'bm-pet-carmine',     petId: 753, cost: 1_550, maxPerPlayer: 1 },
      { id: 'bm-pet-exsanguine',  petId: 754, cost: 1_550, maxPerPlayer: 1 },
      { id: 'bm-pet-redhour',     petId: 755, cost: 1_800, maxPerPlayer: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // LOST MINE EXPEDITION — event 6 of 13
  //
  // The TEMPORARY CONTENT SLOT, which Haunted Realms also needs. "Explore a
  // temporary mine containing relics, treasure rooms and unusual creatures"
  // turned out to be a dig site with an `eventId` — the same gating shape
  // as event fish and enemy variants, now used a third time.
  //
  // ── WHY THE SITE ROWS ARE PERMANENT EVEN THOUGH THE MINE IS NOT ─────
  // `openDigSites()` filters it out when the event is over, but the site and
  // its five relics stay in the data forever. A museum wing filled during
  // the event has to STAY filled — a museum that empties when an event ends
  // is not a museum.
  //
  // ── FASTER AND RICHER THAN A PERMANENT SITE ─────────────────────
  // Stages are shorter and relicChance is 0.18 against a permanent site's
  // 0.10. A fortnight is not long enough to wait out a 1-in-10 on content
  // that disappears, and the dig IS the event rather than a side loop.
  {
    id: 'lost-mine-2027',
    name: 'Lost Mine Expedition',
    description: 'A sealed shaft in Sunscar has been opened. The survey maps are wrong, and the tally slate is still on the ledge.',
    emoji: '\u26CF\uFE0F',
    color: '#f59e0b',
    startAt: '2027-07-12T00:00:00Z',
    endAt:   '2027-07-26T00:00:00Z',
    multipliers: { rareDrop: 1.2 },
    currency: {
      id: 'lampblack',
      name: 'Lampblack',
      emoji: '🕯\uFE0F',
      sprite: 'lampblack',
      // Digging anywhere counts — but a tile in the Lost Mine is worth four
      // of one in a permanent site, so the event points at its own content.
      earn: { dig: 8, eventDig: 32 },
    },
    track: [
      { at:   120, label: 'Down the shaft',   reward: { gold: 25_000 } },
      { at:   300, label: 'Chip',             reward: { pets: [{ petId: 757 }] } },
      { at:   560, label: 'First seam',       reward: { gold: 60_000, diamonds: 25 } },
      { at:   900, label: 'Quartzback',       reward: { pets: [{ petId: 760 }] } },
      { at: 1_300, label: 'Lower levels',     reward: { gold: 120_000 } },
      { at: 1_750, label: 'Geode',            reward: { pets: [{ petId: 763 }] } },
      { at: 2_250, label: 'The tapping',      reward: { diamonds: 75 } },
      { at: 2_800, label: 'Shalejaw',         reward: { pets: [{ petId: 766 }] } },
      { at: 3_350, label: 'Deep dark',        reward: { gold: 250_000 } },
      { at: 3_950, label: 'The Last Shift',   reward: { pets: [{ petId: 770 }] } },
      { at: 4_450, label: 'Deep Delver',      reward: { titleId: 'deep-delver' } },
      { at: 5_000, label: 'Vein',             reward: { pets: [{ petId: 771 }], diamonds: 150 } },
    ],
    shop: [
      { id: 'lm-pet-sootmouse',  petId: 758, cost:   350, maxPerPlayer: 1 },
      { id: 'lm-pet-tallow',     petId: 759, cost:   350, maxPerPlayer: 1 },
      { id: 'lm-pet-pickpaw',    petId: 761, cost:   600, maxPerPlayer: 1 },
      { id: 'lm-pet-firedamp',   petId: 762, cost:   600, maxPerPlayer: 1 },
      { id: 'lm-pet-knocker',    petId: 764, cost: 1_000, maxPerPlayer: 1 },
      { id: 'lm-pet-cavelight',  petId: 765, cost: 1_000, maxPerPlayer: 1 },
      { id: 'lm-pet-deadfall',   petId: 767, cost: 1_800, maxPerPlayer: 1 },
      { id: 'lm-pet-seam',       petId: 768, cost: 2_200, maxPerPlayer: 1 },
      { id: 'lm-pet-lampwright', petId: 769, cost: 2_800, maxPerPlayer: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // HAUNTED REALMS — event 7 of 13
  //
  // The event that builds the TEMPORARY DUNGEON, the last piece of shared
  // machinery the calendar needed. Echo Realm Rift reuses it directly.
  // Together with the enemy variant layer (Blood Moon) and the temporary
  // dig site (Lost Mine), "a place that is only here this month" is now
  // three content types deep and costs nothing to add a fourth to.
  //
  // ── IT DELIBERATELY DOES NOT OVERLAP BLOOD MOON ───────────────────
  // Ends 2027-11-04; Blood Moon starts 2027-11-05. Adjacent, never
  // concurrent, and that is a hard constraint rather than a preference:
  // `variantFor` returns the FIRST matching variant, so two live variant
  // events would mean one of them silently never appears on an enemy the
  // other had already claimed. The middleware is happy to pay both events
  // at once — the variant layer is what cannot be shared on the same day.
  // Anything scheduled into October-November must respect this.
  //
  // ── THREE WEEKS, AND WHERE THE CURRENCY COMES FROM ───────────────
  // The manor pays `eventDungeon: 450` on a two-attempts-a-day cap, which
  // is roughly 55% of a modest player's daily Candlesouls; haunted kills in
  // the open world are most of the rest. That split is the point — the
  // dungeon is the destination and the haunted world is what you do between
  // runs, so neither alone finishes the track and neither is skippable.
  //
  // An ordinary dungeon pays `dungeonClear: 20`, a fortieth of the manor.
  // Third time this split has been needed and the reasoning has not changed
  // since the fishing event: pay for the event's own content, not for the
  // activity the event happens to resemble.
  {
    id: 'haunted-realms-2027',
    name: 'Haunted Realms',
    description: 'Something has gone wrong with the lights. A house nobody built is standing on the hill, and roughly a third of what lives out there has stopped breathing without noticing.',
    emoji: '🎃',
    color: '#f97316',
    startAt: '2027-10-14T00:00:00Z',
    endAt:   '2027-11-04T00:00:00Z',
    multipliers: { rareDrop: 1.25, xp: 1.15 },
    currency: {
      id: 'candlesoul',
      name: 'Candlesouls',
      emoji: '🕯\uFE0F',
      sprite: 'candlesoul',
      earn: {
        kill: 2,
        eventKill: 25,
        bossKill: 60,
        // The manor. See the note above on why this is 22x an ordinary
        // dungeon rather than a bonus on top of one.
        eventDungeon: 450,
        dungeonClear: 20,
      },
    },
    // Twelve rungs to 24,000 — a modest player (the volume model in
    // .verify/check_event_framework.ts) earns ~34,600 across the window, so
    // the track clears with headroom but the shop still forces a choice.
    track: [
      { at:    600, label: 'The lights go out',   reward: { gold: 40_000 } },
      { at:  1_600, label: 'Rattle',              reward: { pets: [{ petId: 772 }] } },
      { at:  3_000, label: 'A house on the hill', reward: { diamonds: 50 } },
      { at:  4_600, label: 'Keyhole',             reward: { pets: [{ petId: 775 }] } },
      { at:  6_500, label: 'The door is open',    reward: { gold: 180_000 } },
      { at:  8_600, label: 'Nanny Grey',          reward: { pets: [{ petId: 778 }] } },
      { at: 11_000, label: 'Upstairs',            reward: { diamonds: 90, items: [{ itemId: 3511, quantity: 3 }] } },
      { at: 13_600, label: 'Pallbearer',          reward: { pets: [{ petId: 781 }] } },
      { at: 16_400, label: 'The long hallway',    reward: { gold: 400_000 } },
      { at: 19_200, label: 'The Invited',         reward: { titleId: 'invited' } },
      { at: 21_500, label: 'A place at the table', reward: { items: [{ itemId: 3512, quantity: 1 }], diamonds: 120 } },
      {
        at: 24_000, label: 'She comes with you',
        reward: {
          pets: [{ petId: 786 }],
          // ONE PER CLASS — the player is granted theirs. See the Haunted
          // Realms block in data/cosmetics.ts for why a single id would
          // have been a capstone five classes could not wear.
          cosmeticSet: [501, 502, 503, 504, 505, 506, 507],
          diamonds: 200,
        },
      },
    ],
    shop: [
      // The ten pets the track does not hand over. Deliberately the whole
      // rest of the set: a collector who runs the manor every day can
      // complete fifteen pets, and a casual player picks the three or four
      // they like. Nobody is short of something to spend on.
      { id: 'hr-pet-dustsheet',   petId: 773, cost:   300, maxPerPlayer: 1 },
      { id: 'hr-pet-gutter',      petId: 774, cost:   300, maxPerPlayer: 1 },
      { id: 'hr-pet-mothbride',   petId: 776, cost:   550, maxPerPlayer: 1 },
      { id: 'hr-pet-clatterjack', petId: 777, cost:   550, maxPerPlayer: 1 },
      { id: 'hr-pet-hollowhound', petId: 779, cost:   900, maxPerPlayer: 1 },
      { id: 'hr-pet-the-guest',   petId: 780, cost:   900, maxPerPlayer: 1 },
      { id: 'hr-pet-weeping-jane',petId: 782, cost: 1_800, maxPerPlayer: 1 },
      { id: 'hr-pet-grandfather', petId: 783, cost: 2_200, maxPerPlayer: 1 },
      { id: 'hr-pet-mr-nobody',   petId: 784, cost: 2_200, maxPerPlayer: 1 },
      { id: 'hr-pet-candlewake',  petId: 785, cost: 2_400, maxPerPlayer: 1 },
      // Two material lines, so Candlesouls have a floor to spend against
      // once the pets are bought. Repeatable on purpose — a shop whose
      // every line caps at one becomes inert the moment it is cleared, and
      // then the last week of the event pays nothing at all.
      { id: 'hr-mat-ribbon',      itemId: 3511, quantity: 5, cost:   900, maxPerPlayer: 5 },
      { id: 'hr-mat-invitation',  itemId: 3512, quantity: 1, cost: 3_000, maxPerPlayer: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // SPRING HUNT — event 8 of 13
  //
  // Builds HIDDEN CACHES (services/game/springHunt.ts), the fourth and last
  // single-player machine the calendar needs. Echo Realm Rift's portals are
  // the same thing with different paint; after this only Dragon Awakening's
  // server-wide shared goal is unbuilt.
  //
  // ── THE EVENT IS TRAVEL ─────────────────────────────────────
  // Every zone hides a few caches a day and then gives nothing, so clearing
  // the day means walking the whole map. Nothing else in the game asks for
  // that — a zone is somewhere you settle at your level and never leave, and
  // Greenvale is dead to a level-80 player eleven months of the year. For a
  // fortnight every zone you have ever unlocked is worth standing in again.
  //
  // Measured, not guessed (.verify/sim_spring_hunt.ts over 400 days): a full
  // nine-zone sweep costs ~177 qualifying actions and opens ~27 caches, one
  // every 6.5 actions. A zone-1-only player gets 2 caches for 9 actions, so
  // a brand-new character can take part — which a two-week seasonal event
  // has to allow or it is endgame content wearing a party hat.
  //
  // ── WHY THE ORDINARY RATES ARE 1 ──────────────────────────────
  // A kill, gather, catch or dig pays 1 AND feeds the hunt — the only event
  // where an action does both, because here the activity is how you search.
  // They are deliberately token: a find is 150, so 99% of a day's Shells
  // come from finds. The 1 exists only so a player standing in a zone they
  // have already cleared out is not earning literally nothing.
  {
    id: 'spring-hunt-2027',
    name: 'Spring Hunt',
    description: 'Somebody has hidden painted shells across every realm, and left no map. They are not where you would put them.',
    emoji: '🥚',
    color: '#4ade80',
    startAt: '2027-04-07T00:00:00Z',
    endAt:   '2027-04-21T00:00:00Z',
    multipliers: { xp: 1.1, gold: 1.15 },
    currency: {
      id: 'painted-shell',
      name: 'Painted Shells',
      emoji: '🥚',
      sprite: 'painted-shell',
      earn: {
        // Declaring eventFind is what TURNS THE HUNT ON — `huntTick` skips
        // any event without a rate for it, so no other event starts hiding
        // caches by accident.
        eventFind: 150,
        kill: 1, gather: 1, fish: 1, dig: 1,
      },
    },
    // Twelve rungs to 14,000. A modest player (3 zones a day, the volume
    // model's 9 finds) earns ~20,800 over the fortnight; a dedicated one
    // sweeping all nine ~62,500 against 32,000 all-in.
    track: [
      { at:    400, label: 'First thaw',        reward: { gold: 30_000 } },
      { at:  1_000, label: 'Pip',               reward: { pets: [{ petId: 787 }] } },
      { at:  1_800, label: 'Green shoots',      reward: { diamonds: 40 } },
      { at:  2_700, label: 'Thaw',              reward: { pets: [{ petId: 790 }] } },
      { at:  3_800, label: 'The hedgerows',     reward: { gold: 140_000 } },
      { at:  5_000, label: 'Mossback',          reward: { pets: [{ petId: 793 }] } },
      { at:  6_400, label: 'Every realm',       reward: { diamonds: 80, items: [{ itemId: 3514, quantity: 3 }] } },
      { at:  7_900, label: 'Rainkeeper',        reward: { pets: [{ petId: 796 }] } },
      { at:  9_500, label: 'The long walk',     reward: { gold: 320_000 } },
      { at: 11_200, label: 'Thawbringer',       reward: { titleId: 'thawbringer' } },
      { at: 12_600, label: 'The unhatched',     reward: { items: [{ itemId: 3515, quantity: 1 }], diamonds: 110 } },
      {
        at: 14_000, label: 'Verdance',
        reward: {
          pets: [{ petId: 801 }],
          cosmeticSet: [508, 509, 510, 511, 512, 513, 514],
          diamonds: 180,
        },
      },
    ],
    shop: [
      { id: 'sh-pet-sprig',        petId: 788, cost:   250, maxPerPlayer: 1 },
      { id: 'sh-pet-tufty',        petId: 789, cost:   250, maxPerPlayer: 1 },
      { id: 'sh-pet-bramblekit',   petId: 791, cost:   450, maxPerPlayer: 1 },
      { id: 'sh-pet-dandelion',    petId: 792, cost:   450, maxPerPlayer: 1 },
      { id: 'sh-pet-shellsong',    petId: 794, cost:   750, maxPerPlayer: 1 },
      { id: 'sh-pet-grubble',      petId: 795, cost:   750, maxPerPlayer: 1 },
      { id: 'sh-pet-firstlight',   petId: 797, cost: 1_400, maxPerPlayer: 1 },
      { id: 'sh-pet-greenfinger',  petId: 798, cost: 1_700, maxPerPlayer: 1 },
      { id: 'sh-pet-thawherd',     petId: 799, cost: 1_700, maxPerPlayer: 1 },
      { id: 'sh-pet-sowling',      petId: 800, cost: 1_900, maxPerPlayer: 1 },
      // Repeatable lines so the last week still has somewhere to put Shells.
      { id: 'sh-mat-shell',        itemId: 3513, quantity: 10, cost:   600, maxPerPlayer: 6 },
      { id: 'sh-mat-firstgreen',   itemId: 3514, quantity:  3, cost: 1_100, maxPerPlayer: 5 },
      { id: 'sh-mat-unhatched',    itemId: 3515, quantity:  1, cost: 2_800, maxPerPlayer: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // REALM ANNIVERSARY — event 9 of 13
  //
  // Builds PORTRAIT FRAMES (data/portraitFrames.ts), the first
  // class-agnostic cosmetic. That is the important thing in this event, and
  // it is not really about the anniversary:
  //
  //   Every cosmetic until now was a class-locked skin, so an event
  //   cosmetic had to ship as a set of SEVEN to reward everybody. One frame
  //   is one piece of art that works for all seven classes, both genders
  //   and every skin underneath — cheaper than a skin set AND it reaches
  //   the whole playerbase. It is also the unit the rotating cosmetic store
  //   needs for a daily lane, which could not exist while cosmetics came in
  //   sevens.
  //
  // So this event gives FOUR frames rather than one cape and one frame: at
  // a frame's cost, four is still less art than any previous event's
  // cosmetics, and four rungs of visible prestige suits an anniversary far
  // better than one.
  //
  // ── THE BOSS RUSH IS A DUNGEON ─────────────────────────────
  // The Hall of Years — see data/dungeons.ts. Nine rooms, every one a real
  // zone boss at YOUR level with its real ability kit, which costs no new
  // enemies and no new combat code because `scaleDungeonEnemy` already
  // rebuilds a dungeon enemy from the level curve.
  //
  // It did cost one real fix: the dungeon kill path credited
  // `recordEnemyKill` + `progressQuests`, so killing the Echo Sovereign in
  // here would have handed a level-60 player the Zone 9 passage crest.
  // Event dungeons now credit nothing permanent (BattleScreen).
  //
  // ── IT IS A BOSS EVENT, NOT A RUSH EVENT ─────────────────────
  // One rush a day cannot fund a fortnight on its own, and padding the cap
  // to three would turn the headline into a chore. So the ordinary ZONE
  // BOSSES pay 300 each — the event sends you back to all nine out in the
  // world, and the rush is the one-a-day centrepiece worth four of them.
  // The volume model in check_event_framework.ts carries an override for
  // this event saying so, because scoring a boss event against a generic
  // half-a-boss-a-day would call its own track unclearable.
  {
    id: 'realm-anniversary-2027',
    name: 'Realm Anniversary',
    description: 'A year of the realms. Everything you have already beaten is back, at your size, and somebody has put up bunting.',
    emoji: '🎂',
    color: '#fbbf24',
    startAt: '2027-06-02T00:00:00Z',
    endAt:   '2027-06-16T00:00:00Z',
    multipliers: { gold: 1.25, xp: 1.25 },
    currency: {
      id: 'year-mark',
      name: 'Year-Marks',
      emoji: '🪙',
      sprite: 'year-mark',
      earn: {
        // The core activity: go and fight your own history.
        bossKill: 300,
        // The Hall of Years, once a day. Worth four zone bosses.
        eventDungeon: 1_200,
        kill: 1,
        dungeonClear: 15,
      },
    },
    // Twelve rungs to 30,000. Modest ~43,200 over the fortnight against
    // 54,200 all-in, so the track clears and the shop still forces a
    // choice. Four frames across the track and the shop.
    track: [
      { at:  1_000, label: 'Bunting up',        reward: { gold: 60_000 } },
      { at:  2_400, label: 'One Candle',        reward: { frameId: 'anniversary-candle' } },
      { at:  4_200, label: 'Tally',             reward: { pets: [{ petId: 802 }] } },
      { at:  6_400, label: 'The first year',    reward: { diamonds: 60 } },
      { at:  9_000, label: 'Candlewick',        reward: { pets: [{ petId: 804 }] } },
      { at: 12_000, label: 'Everything beaten', reward: { gold: 260_000 } },
      { at: 15_000, label: 'Echolet',           reward: { pets: [{ petId: 806 }] } },
      { at: 18_000, label: 'The long hall',     reward: { diamonds: 110, items: [{ itemId: 3517, quantity: 3 }] } },
      { at: 21_000, label: 'Ledgerwing',        reward: { pets: [{ petId: 808 }] } },
      { at: 24_000, label: "Founders' Laurel",  reward: { frameId: 'anniversary-laurel', titleId: 'of-the-first-year' } },
      { at: 27_000, label: 'The ledger',        reward: { items: [{ itemId: 3518, quantity: 1 }], diamonds: 140 } },
      {
        at: 30_000, label: 'First Light',
        reward: {
          pets: [{ petId: 811 }],
          frameId: 'anniversary-firstlight',
          diamonds: 250,
        },
      },
    ],
    shop: [
      { id: 'ra-pet-streamer',   petId: 803, cost:   400, maxPerPlayer: 1 },
      { id: 'ra-pet-keepsake',   petId: 805, cost:   700, maxPerPlayer: 1 },
      { id: 'ra-pet-firstborn',  petId: 807, cost: 1_200, maxPerPlayer: 1 },
      { id: 'ra-pet-rememberer', petId: 809, cost: 2_400, maxPerPlayer: 1 },
      { id: 'ra-pet-yearhound',  petId: 810, cost: 2_400, maxPerPlayer: 1 },
      // The fourth frame is the one thing in the shop you cannot also get
      // from the track — deliberately, so the shop has a headline of its
      // own rather than being the track's leftovers.
      { id: 'ra-frame-garland',  frameId: 'anniversary-garland', cost: 6_000, maxPerPlayer: 1 },
      { id: 'ra-mat-yearmark',   itemId: 3516, quantity: 10, cost:   900, maxPerPlayer: 6 },
      { id: 'ra-mat-coin',       itemId: 3517, quantity:  3, cost: 1_600, maxPerPlayer: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // FORGEWORKS — event 10 of 13
  //
  // Builds the TEMPORARY RECIPE, the fifth and last use of the eventId
  // gating shape (event fish, enemy variants, dig site, dungeon, recipes).
  // Nothing in the remaining three events needs a sixth.
  //
  // ── THE BENCH IS A CHAIN, NOT A SHOPPING LIST ─────────────────────
  // Five recipes that exist only while the event runs, and four of them
  // consume the previous one's output: Ingot → Cogtooth → Phial → Core →
  // Spring. A crafting event whose recipes all draw straight from the
  // world's existing materials is a list of things to go buy ingredients
  // for; a chain means the event's own output is its own input, which is
  // what a forge fortnight should actually feel like.
  //
  // They sit at the FORGE rather than in a pop-up workshop: a station that
  // appears for two weeks and then vanishes from the town is a much bigger
  // promise than this event is making.
  //
  // ── eventCraft, FOR THE FIFTH TIME ──────────────────────────────
  // A bench craft pays 220, any other craft 8. Without the split the event
  // would reward CRAFTING rather than rewarding Forgeworks, and the
  // commission bench would be the slowest way to play its own event —
  // exactly what the fishing week taught us.
  //
  // The craft signal had to learn WHICH recipe: `incrementCrafted` carried
  // only a count. Its payload now optionally carries the recipe id, and
  // both of its two dispatch sites already knew it.
  {
    id: 'forgeworks-2027',
    name: 'Forgeworks',
    description: 'The old commission bench is lit again and the work orders are stacking up. Two weeks, and then the fires go out.',
    emoji: '⚙️',
    color: '#f59e0b',
    startAt: '2027-08-16T00:00:00Z',
    endAt:   '2027-08-30T00:00:00Z',
    multipliers: { xp: 1.15 },
    currency: {
      id: 'work-order',
      name: 'Work Orders',
      emoji: '📜',
      sprite: 'work-order',
      earn: {
        // The bench.
        eventCraft: 220,
        // Ordinary crafting still counts for something — a player mid-way
        // through a normal recipe when the event starts is not wasting it.
        craft: 8,
        // The professions that FEED the bench. Deliberately paid, because
        // a crafting event that ignores gathering asks you to buy every
        // ingredient off the market.
        gather: 3,
      },
    },
    // Twelve rungs to 26,000. A modest player crafting ~10 bench items and
    // 40 gathers a day earns ~2,320/day, ~32,500 across the fortnight.
    track: [
      { at:    700, label: 'Fires lit',          reward: { gold: 50_000 } },
      { at:  1_800, label: 'Sprocket',           reward: { pets: [{ petId: 812 }] } },
      { at:  3_200, label: 'First commission',   reward: { diamonds: 50 } },
      { at:  5_000, label: 'Gaugewing',          reward: { pets: [{ petId: 815 }] } },
      { at:  7_200, label: 'Running hot',        reward: { gold: 220_000 } },
      { at:  9_800, label: 'Overseer',           reward: { pets: [{ petId: 818 }] } },
      { at: 12_600, label: 'Tolerances held',    reward: { diamonds: 100, items: [{ itemId: 3521, quantity: 2 }] } },
      { at: 15_600, label: 'The Governor',       reward: { pets: [{ petId: 821 }] } },
      { at: 18_600, label: 'Night shift',        reward: { gold: 380_000 } },
      { at: 21_400, label: 'Master of the Bench', reward: { titleId: 'master-of-the-bench' } },
      { at: 23_800, label: 'Signed off',         reward: { items: [{ itemId: 3523, quantity: 1 }], diamonds: 130 } },
      {
        at: 26_000, label: 'The Perpetual',
        reward: {
          pets: [{ petId: 826 }],
          // ONE frame, not seven class skins. See the Forgeworks block in
          // data/portraitFrames.ts.
          frameId: 'forgeworks-caliper',
          diamonds: 220,
        },
      },
    ],
    shop: [
      { id: 'fw-pet-bellows',    petId: 813, cost:   300, maxPerPlayer: 1 },
      { id: 'fw-pet-tapper',     petId: 814, cost:   300, maxPerPlayer: 1 },
      { id: 'fw-pet-rivet',      petId: 816, cost:   550, maxPerPlayer: 1 },
      { id: 'fw-pet-flux',       petId: 817, cost:   550, maxPerPlayer: 1 },
      { id: 'fw-pet-clank',      petId: 819, cost:   900, maxPerPlayer: 1 },
      { id: 'fw-pet-quenchling', petId: 820, cost:   900, maxPerPlayer: 1 },
      { id: 'fw-pet-pigiron',    petId: 822, cost: 1_700, maxPerPlayer: 1 },
      { id: 'fw-pet-whistle',    petId: 823, cost: 1_900, maxPerPlayer: 1 },
      { id: 'fw-pet-mainspring', petId: 824, cost: 1_900, maxPerPlayer: 1 },
      { id: 'fw-pet-toolmark',   petId: 825, cost: 2_100, maxPerPlayer: 1 },
      // The bench's own inputs, for a player who would rather buy the
      // bottleneck than farm it. Repeatable, so Work Orders still have
      // somewhere to go once the pets are bought.
      { id: 'fw-mat-cogtooth',   itemId: 3519, quantity: 20, cost:   700, maxPerPlayer: 6 },
      { id: 'fw-mat-core',       itemId: 3521, quantity:  2, cost: 1_900, maxPerPlayer: 4 },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════
  // TWISTED TOWER — event 11 of 13
  //
  // Builds DAILY RULE ROTATION (services/game/towerTwists.ts). The Endless
  // Tower is the most repetitive thing in the game on purpose — a ladder of
  // the same fight with bigger numbers — which is fine as a ceiling chase
  // and poor as an event. So this changes the RULE, not the ladder: same
  // tower, same floors, one modifier a day, and the climb has to be read
  // again each morning.
  //
  // ── EVERY TWIST TRADES ────────────────────────────────────────────────
  // A pure buff is a free day and a pure penalty is a day off. Ponderous
  // doubles the pay for 40% more enemy HP; Hollow makes the climb easy and
  // pays 40% less; Gilded triples the pay and nearly doubles the danger.
  // The question is always "is today's trade good for MY class", never "is
  // today worth logging in".
  //
  // ── THE TWIST SCALES THE PAYOUT ───────────────────────────────────────
  // `eventTowerFloor` is paid per floor and multiplied by the day's
  // `rewardMult`, so the trade is real currency rather than flavour text.
  // No ordinary-activity split is needed: the tower paid an event nothing
  // before this, so there is no twin to split from — the same shape as
  // Spring Hunt's `eventFind`.
  {
    id: 'twisted-tower-2027',
    name: 'Twisted Tower',
    description: 'Something has got into the Endless Tower and it is not the same climb twice. Every morning it has a new rule.',
    emoji: '🗼',
    color: '#38bdf8',
    startAt: '2027-09-13T00:00:00Z',
    endAt:   '2027-09-27T00:00:00Z',
    multipliers: { xp: 1.1 },
    currency: {
      id: 'warped-sigil',
      name: 'Warped Sigils',
      emoji: '🌀',
      sprite: 'warped-sigil',
      earn: {
        // Per FLOOR, before the day's twist multiplies it.
        eventTowerFloor: 160,
      },
    },
    // Twelve rungs to 22,000. The volume model climbs 12 floors a day at an
    // average twist multiplier of ~1.3, so a modest climber earns ~2,500 a
    // day and ~35,000 across the fortnight.
    track: [
      { at:    600, label: 'The first wrong step',  reward: { gold: 45_000 } },
      { at:  1_500, label: 'Rungling',              reward: { pets: [{ petId: 827 }] } },
      { at:  2_700, label: 'It changed overnight',  reward: { diamonds: 45 } },
      { at:  4_200, label: 'Tallykeep',             reward: { pets: [{ petId: 829 }] } },
      { at:  6_000, label: 'Higher than yesterday', reward: { gold: 200_000 } },
      { at:  8_200, label: 'Banister',              reward: { pets: [{ petId: 831 }] } },
      { at: 10_600, label: 'Reading the rule',      reward: { diamonds: 95 } },
      { at: 13_200, label: 'Skewloft',              reward: { pets: [{ petId: 833 }] } },
      { at: 15_800, label: 'The long flight',       reward: { gold: 340_000 } },
      { at: 18_200, label: 'Rule of the Day',       reward: { titleId: 'rule-of-the-day' } },
      { at: 20_200, label: 'Capstone',              reward: { pets: [{ petId: 834 }], diamonds: 120 } },
      {
        at: 22_000, label: 'The Spire',
        reward: {
          pets: [{ petId: 836 }],
          frameId: 'twisted-spire',
          diamonds: 200,
        },
      },
    ],
    shop: [
      { id: 'tt-pet-plumbline', petId: 828, cost:   300, maxPerPlayer: 1 },
      { id: 'tt-pet-vertigo',   petId: 830, cost:   550, maxPerPlayer: 1 },
      { id: 'tt-pet-landing',   petId: 832, cost:   900, maxPerPlayer: 1 },
      { id: 'tt-pet-lintel',    petId: 835, cost: 1_900, maxPerPlayer: 1 },
      // Repeatable lines, so Sigils keep a floor to spend against once the
      // four pets are bought and the last days still pay for something.
      { id: 'tt-mat-cogtooth',  itemId: 3519, quantity: 15, cost:   800, maxPerPlayer: 6 },
      { id: 'tt-mat-core',      itemId: 3521, quantity:  2, cost: 2_000, maxPerPlayer: 5 },
    ],
  },
  // ── 12. ECHO REALM RIFT ───────────────────────────────────────────────
  // Reuses BOTH existing machines rather than adding a third: the hidden-cache
  // system (springHunt.ts) for the portals and the variant layer
  // (enemyVariants.ts) for the distorted bestiary. A portal is a cache that
  // fights back, which is what EVENTS_PLAN.md predicted, and it turned out to
  // be true of the code as well as the fiction.
  //
  // ── THE SCHEDULE IS A HARD CONSTRAINT, NOT A PREFERENCE ───────────────
  // `variantFor` returns the FIRST matching variant, so two overlapping
  // variant events mean one of them silently never appears. Haunted Realms
  // runs to 2027-11-04 and Blood Moon 11-05 to 11-09, so the Rift opens on
  // 11-29 \u2014 clear of both, with three weeks of air after Blood Moon.
  // `.verify/check_event_dungeon.ts` asserts the no-overlap rule.
  //
  // ── WHY THE PORTALS ARE RARER THAN SPRING HUNT'S EGGS ─────────────────
  // Spring Hunt hides 2-4 caches per zone, 4-9 actions apart: a wide shallow
  // sweep, because its pitch is that every zone you ever unlocked is worth
  // standing in again. The Rift hides 1-3 per zone, 7-14 apart, and pays more
  // for each. Same machine, opposite texture \u2014 the Hunt is a walk and the
  // Rift is a dig. The tuning lives on the event because the seed namespace is
  // the event id, so the two cannot hide in the same places even on a day both
  // ran (they never do, but the collision was real before this: the seed was
  // the literal string 'spring-hunt:' and the bounds were module constants).
  {
    id: 'echo-realm-rift-2027',
    name: 'Echo Realm Rift',
    description: 'The Echo Realm is leaking. Holes open where nobody is looking, and the things nearby come back not quite right.',
    emoji: '\u{1F300}',
    color: '#a855f7',
    startAt: '2027-11-29T00:00:00Z',
    endAt:   '2027-12-13T00:00:00Z',
    multipliers: { rareDrop: 1.15 },
    // Rarer portals, wider windows. See the note above.
    hunt: { cacheMin: 1, cacheMax: 3, windowMin: 7, windowMax: 14 },
    currency: {
      id: 'rift-shard',
      name: 'Rift Shards',
      emoji: '\u{1F4A0}',
      sprite: 'rift-shard',
      earn: {
        // ── CALIBRATED AGAINST THE HOUSE BENCHMARK, NOT GUESSED ─────────
        // My first pass was eventFind 180 / eventKill 55 / kill 8, which
        // check_event_framework rejected: a MODEST player would have earned
        // ~62,000 against a 43,300 all-in, so there was no choice to make in
        // the shop and the whole fortnight was over in the first week. That
        // probe models a modest player at 9 portals and 120 fights a day
        // (40 Distorted + 80 ordinary at the 0.30 share) and applies the same
        // model to all twelve events, so it — not my arithmetic — is the
        // standard these have to meet.
        //
        // The cut fell almost entirely on the KILL rates, which is also the
        // right design call: the Rift is about closing portals. Kills are the
        // trickle that keeps a player who cannot travel today from earning
        // nothing, and they should not be able to out-earn the event's own
        // content by swinging at the nearest slime.
        //
        // A closed portal is the event's own content and pays like it —
        // deliberately above Spring Hunt's 150, because the Rift hides fewer
        // and spaces them further apart.
        eventFind: 190,
        // A Distorted kill pays instead of `kill`, not on top of it.
        eventKill: 22,
        // An ordinary kill still pays something, so a player who cannot
        // travel (or has cleared today's portals) is never earning zero.
        kill: 3,
      },
    },
    // Twelve rungs to 22,000, the same ceiling as Twisted Tower and
    // Forgeworks. Volume model: ~5 portals a day (900) plus ~40 kills of
    // which 30% are Distorted (12 x 55 + 28 x 8 = 884) is ~1,780 a day and
    // ~25,000 over the fortnight \u2014 so the capstone lands for a daily
    // player with room to spare and is out of reach for a drive-by.
    track: [
      { at:    600, label: 'First hole closed',   reward: { gold: 45_000 } },
      { at:  1_500, label: 'Offcut',              reward: { pets: [{ petId: 837 }] } },
      { at:  2_700, label: 'Seeing double',       reward: { diamonds: 45 } },
      { at:  4_200, label: 'Mirrorling',          reward: { pets: [{ petId: 839 }] } },
      { at:  6_000, label: 'Out of phase',        reward: { gold: 200_000 } },
      { at:  8_200, label: 'Refrain',             reward: { pets: [{ petId: 842 }] } },
      { at: 10_600, label: 'The same note twice', reward: { diamonds: 95 } },
      { at: 13_200, label: 'Shutterling',         reward: { pets: [{ petId: 844 }] } },
      { at: 15_800, label: 'Wider every day',     reward: { gold: 340_000 } },
      { at: 18_200, label: 'Rift Closer',         reward: { titleId: 'rift-closer' } },
      { at: 20_200, label: 'Afterimage',          reward: { pets: [{ petId: 847 }], diamonds: 120 } },
      {
        at: 22_000, label: "The Sovereign's Echo",
        reward: {
          pets: [{ petId: 848 }],
          frameId: 'rift-horizon',
          diamonds: 200,
        },
      },
    ],
    shop: [
      { id: 'err-pet-second',    petId: 838, cost:   300, maxPerPlayer: 1 },
      { id: 'err-pet-fetch',     petId: 840, cost:   550, maxPerPlayer: 1 },
      { id: 'err-pet-hollow',    petId: 841, cost:   750, maxPerPlayer: 1 },
      { id: 'err-pet-other',     petId: 843, cost: 1_100, maxPerPlayer: 1 },
      { id: 'err-pet-paradox',   petId: 845, cost: 1_700, maxPerPlayer: 1 },
      { id: 'err-pet-recursion', petId: 846, cost: 2_100, maxPerPlayer: 1 },
      // Repeatable, so Shards keep something to buy once the six pets are
      // owned and the last days of the fortnight still pay for something.
      { id: 'err-mat-splinter',  itemId: 3524, quantity: 15, cost:   800, maxPerPlayer: 6 },
      { id: 'err-mat-horizon',   itemId: 3526, quantity:  2, cost: 2_000, maxPerPlayer: 5 },
    ],
  },
  // ── 13. DRAGON AWAKENING ── THE LAST ONE ────────────────────
  // The only event in the calendar with a SERVER component, and the only new
  // system left after twelve: migration 0128's shared counter plus
  // services/eventGoal.ts. Everything else here is the framework the other
  // twelve already use.
  //
  // ── WHY THE GOAL GATES RUNGS AND NOT A DUNGEON ───────────────
  // The plan said the hatch should open a world boss. A temporary dungeon is
  // the machinery that would carry that (Haunted Realms has one), but
  // `openDungeons` is a pure function over the live event ids, and threading
  // an async server fact into it would mean either a second gate nothing
  // reads or a service cache inside a data module.
  //
  // So the hatch gates the three track rungs the DRAGON ITSELF pays. It is
  // the same statement — the server's progress changes what you can have —
  // through machinery that already exists, read in exactly one place, and
  // assertable. `requiresGoal` has one reader and a probe that says so.
  //
  // ── EARN IS DELIBERATELY BROAD ──────────────────────────
  // Every other event narrows to one activity on purpose — a fishing event
  // pays for fish. This one is the opposite and has to be: it is a COMMUNITY
  // goal, so the pitch is that whatever you were already doing counts toward
  // it. Narrowing it would mean a player who does not happen to enjoy the
  // chosen activity cannot help hatch the server's egg, which is the one
  // thing this event is about. There is no event-content split for the same
  // reason: there is no event content, there is just everybody playing.
  {
    id: 'dragon-awakening-2027',
    name: 'Dragon Awakening',
    description: 'There is an egg in the caldera the size of a house, and it has started to move. Everything anyone does is being counted.',
    emoji: '\u{1F409}',
    color: '#ef4444',
    startAt: '2027-12-27T00:00:00Z',
    endAt:   '2028-01-10T00:00:00Z',
    multipliers: { gold: 1.1, xp: 1.1 },
    serverGoal: {
      // THE ONE NUMBER HERE THAT DEPENDS ON POPULATION, and so the one to
      // revisit before the event runs. 500,000 is about fifty committed
      // players over the fortnight on the volumes check_event_framework
      // models. Too high and the realm watches a bar it cannot fill; too low
      // and it hatches on day two and the remaining twelve days have no
      // shared story. There is no way to derive it from the code — it is a
      // bet on how many people are playing — so it is called out rather
      // than buried.
      threshold: 500_000,
      // The same goal for one person with no server: local-only mode and
      // guests. Reachable by a dedicated player (~31,500 earned over the
      // fortnight) and just out of a modest one's reach (~10,500), so it is
      // a real target either way rather than a consolation.
      solo: 12_000,
      noun: 'the egg',
      effect: 'Hatching it unlocks the final three rungs of the track for everyone.',
    },
    currency: {
      id: 'dragon-scale',
      name: 'Dragon Scales',
      emoji: '\u{1F536}',
      sprite: 'dragon-scale',
      earn: {
        // Broad by design — see the note above. Rates are low per action
        // precisely because every action counts.
        kill: 6,
        bossKill: 60,
        gather: 4,
        craft: 6,
        dungeonClear: 40,
      },
    },
    // Twelve rungs to 7,000 — far below the other fortnight events because
    // the currency is earned from EVERYTHING, so a day's ordinary play pays
    // ~750 rather than the ~2,800 a focused event pays. A modest player
    // clears the track (~10,500 over the fortnight) and still cannot buy the
    // shop out.
    track: [
      { at:   200, label: 'Something moved',     reward: { gold: 50_000 } },
      { at:   500, label: 'Shellchip',           reward: { pets: [{ petId: 849 }] } },
      { at:   900, label: 'The realm is called', reward: { diamonds: 45 } },
      { at: 1_400, label: 'Nestward',            reward: { pets: [{ petId: 851 }] } },
      { at: 2_000, label: 'A crack, audible',    reward: { gold: 220_000 } },
      { at: 2_700, label: 'Wyrmling',            reward: { pets: [{ petId: 854 }] } },
      { at: 3_500, label: 'It answers',          reward: { diamonds: 95 } },
      { at: 4_300, label: 'Scalekeep',           reward: { pets: [{ petId: 856 }] } },
      { at: 5_200, label: 'The Hatchwatch',      reward: { titleId: 'the-hatchwatch' }, requiresGoal: true },
      { at: 6_000, label: 'Firstborn',           reward: { pets: [{ petId: 859 }], gold: 360_000 }, requiresGoal: true },
      { at: 6_500, label: 'Brooder',             reward: { pets: [{ petId: 857 }], diamonds: 120 } },
      {
        at: 7_000, label: 'The Awakened',
        reward: {
          pets: [{ petId: 860 }],
          frameId: 'dragon-crown',
          diamonds: 200,
        },
        requiresGoal: true,
      },
    ],
    shop: [
      { id: 'da-pet-kindling',  petId: 850, cost:   250, maxPerPlayer: 1 },
      { id: 'da-pet-ember',     petId: 852, cost:   450, maxPerPlayer: 1 },
      { id: 'da-pet-hoardling', petId: 853, cost:   700, maxPerPlayer: 1 },
      { id: 'da-pet-cinder',    petId: 855, cost: 1_000, maxPerPlayer: 1 },
      { id: 'da-pet-thermal',   petId: 858, cost: 1_500, maxPerPlayer: 1 },
      // Repeatable, so Scales keep a floor to spend against after the pets.
      { id: 'da-mat-shell',     itemId: 3528, quantity: 15, cost:   700, maxPerPlayer: 6 },
      { id: 'da-mat-scale',     itemId: 3530, quantity:  2, cost: 1_600, maxPerPlayer: 4 },
    ],
  },
]

/**
 * Optional runtime overrides — lets a dev or admin force-activate
 * specific events without editing this file. Persisted in localStorage
 * so a page refresh keeps the forced state.
 *
 * Usage from the browser console:
 *   __forceEvent('goldrush-2026-07', true)   // force ON
 *   __forceEvent('goldrush-2026-07', false)  // force OFF
 *   __forceEvent(null)                       // clear all overrides
 *
 * Attached to `window` in src/main.tsx, alongside __listEvents() and
 * __activeEvents(). (This line used to name an `attachEventOverrides()`
 * helper that does not exist — the wiring is inline there.)
 */
const OVERRIDE_KEY = 'elderglen.eventOverrides'

function readOverrides(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(OVERRIDE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function writeOverrides(map: Record<string, boolean>): void {
  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(map))
  } catch {
    // localStorage might be unavailable (private browsing) — silent no-op.
  }
}

/**
 * The overrides as they stand. The admin dashboard's event panel needs to
 * show which way each switch is thrown, and it is the same localStorage key
 * either way — admin.html and the game are one origin.
 */
export function readEventOverrides(): Record<string, boolean> {
  return readOverrides()
}

export function forceEventOverride(id: string | null, value?: boolean): Record<string, boolean> {
  if (id === null) {
    writeOverrides({})
    return {}
  }
  const cur = readOverrides()
  if (value === undefined) delete cur[id]
  else cur[id] = value
  writeOverrides(cur)
  return cur
}

/** Returns every event currently within its start..end window — or
 *  forced-active via the override layer. */
export function getActiveEvents(now: number = Date.now()): GameEvent[] {
  const overrides = readOverrides()
  return EVENTS.filter(e => {
    if (overrides[e.id] === false) return false  // explicit force-off
    if (overrides[e.id] === true)  return true   // explicit force-on
    const s = Date.parse(e.startAt)
    const en = Date.parse(e.endAt)
    return now >= s && now < en
  })
}

/** Returns the next upcoming event (for "coming soon" hint), or null. */
export function getNextEvent(now: number = Date.now()): GameEvent | null {
  const upcoming = EVENTS
    .filter(e => Date.parse(e.startAt) > now)
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt))
  return upcoming[0] ?? null
}

/** Composed multipliers from all active events — drives bonuses.ts. */
export interface EventMultipliers {
  gold: number
  xp: number
  petXp: number
  skillXp: number
  eggDrop: number
  rareDrop: number
}

export function getEventMultipliers(now: number = Date.now()): EventMultipliers {
  const out: EventMultipliers = {
    gold: 1, xp: 1, petXp: 1, skillXp: 1, eggDrop: 1, rareDrop: 1,
  }
  for (const e of getActiveEvents(now)) {
    for (const [k, v] of Object.entries(e.multipliers)) {
      if (v === undefined) continue
      // Stack multiplicatively so two active events compound naturally.
      out[k as keyof EventMultipliers] *= v
    }
  }
  return out
}
